import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { Country } from '../../../models/country.model';
import { Address } from '../../../models/address.model';
import { CountryState } from '../../../interfaces/country-state';
import { ConfigService } from '../../../services/config.service';
import {
  AddressResult,
  AddressService,
} from '../../../services/address.service';
import { isEmptyArray } from '../../../shared/utils';
import { AuthService } from '../../../services/auth.service';

// pattern="^\d{5}(?:[\-]\d{4})?$" required
const ZIP_PATTERN = /^\d{5}(?:[\-]\d{4})?$/;

/**
 * zip validator only for US
 */
function zipCodeValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const countryCode = control.root.value['country'];
    if (!countryCode) {
      return null;
    } else if (countryCode === 'US') {
      if (!control.value) {
        return { required: 'required' };
      } else if (!ZIP_PATTERN.test(control.value)) {
        return { pattern: 'invalid' };
      }
    }
    return null;
  };
}

@UntilDestroy()
@Component({
  selector: 'app-add-address-modal',
  templateUrl: './address-modal.component.html',
  styleUrls: ['./address-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AddressModalComponent implements OnInit {
  selectedAddress: Address;
  isDeliveryAddress: boolean;

  states: { abbr: string; label: string }[] = [];
  inProgress = false;
  isNewAddress = true;

  suggestedAddresses: Address[] = [];
  selectedSuggestionUUID: string;
  isExistedAddress = false;

  addressForm = new FormGroup({
    label: new FormControl('', [Validators.required]),
    street_1: new FormControl('', [Validators.required]),
    street_2: new FormControl(''),
    city: new FormControl('', [Validators.required]),
    state: new FormControl(''),
    zip_code: new FormControl('', [zipCodeValidator()]),
    country: new FormControl(''),
    state_list: new FormControl(),
  });
  stateFieldTouch = false;

  constructor(
    private configService: ConfigService,
    private addressService: AddressService,
    private activeModal: NgbActiveModal,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.initAddressFields();
  }

  close(address?: Address) {
    this.activeModal.close(address);
  }

  get label() {
    return this.addressForm.get('label');
  }

  get street_1() {
    return this.addressForm.get('street_1');
  }

  get street_2() {
    return this.addressForm.get('street_2');
  }

  get city() {
    return this.addressForm.get('city');
  }

  get zip_code() {
    return this.addressForm.get('zip_code');
  }

  get state() {
    return this.addressForm.get('state');
  }

  get state_list() {
    return this.addressForm.get('state_list');
  }

  get country() {
    return this.addressForm.get('country');
  }

  validationClassName(control: AbstractControl) {
    if (control.touched || control.dirty) {
      return control.invalid ? 'has-danger' : '';
    }
    return '';
  }

  invalidField(control: AbstractControl) {
    return control.errors && (control.touched || control.dirty);
  }

  selectCountry(event: Event) {
    if (event instanceof Country) {
      this.states = event.states;
      this.state_list.setValue('');
      this.state.setValue('');
      this.zip_code.setValue('');
      this.zip_code.markAsUntouched();
    }
  }

  selectState($event: Event | {}) {
    this.stateFieldTouch = true;
    this.selectedAddress.state = $event['abbr'] || '';
    this.state.setValue(this.selectedAddress.state);
  }

  get isStateFieldValid(): boolean {
    if (this.addressForm.value['country'] === 'US') {
      return !!this.state_list.value;
    } else {
      return true;
    }
  }

  get countries(): Country[] {
    return this.configService.countries;
  }

  initAddressFields() {
    this.selectedAddress = this.selectedAddress
      ? new Address(this.selectedAddress)
      : new Address({ id: 0, country_code: 'US' });
    this.isNewAddress = !this.selectedAddress.id;

    this.label.setValue(this.selectedAddress.label);
    this.street_1.setValue(this.selectedAddress.street_1);
    this.street_2.setValue(this.selectedAddress.street_2);
    this.city.setValue(this.selectedAddress.city);
    this.country.setValue(this.selectedAddress.country_code);
    this.state.setValue(this.selectedAddress.state);
    this.state_list.setValue(this.selectedAddress.state);
    this.zip_code.setValue(this.selectedAddress.zip_code);

    this.states = this.getStatesByCountryCode(
      this.selectedAddress.country_code
    );
    this.stateFieldTouch = false;
  }

  saveAddress() {
    this.stateFieldTouch = true;

    if (this.addressForm.valid && this.isStateFieldValid) {
      this.suggestedAddresses = [];
      this.selectedSuggestionUUID = null;
      const addressId = this.selectedAddress.id;
      const type = this.selectedAddress.type;

      this.selectedAddress = new Address({
        ...this.addressForm.value,
        is_default: this.selectedAddress.is_default,
        wslr_ids: [...this.selectedAddress.wslr_ids],
      });

      if (addressId) {
        this.selectedAddress.id = addressId;
      }
      if (type) {
        this.selectedAddress.type = type;
      }

      if (this.states.length > 0) {
        this.selectedAddress.state = this.selectedAddress['state_list'] || '';
      } else {
        this.selectedAddress.state = this.selectedAddress.state || '';
      }

      this.selectedAddress.country_code = this.addressForm.value['country'];

      this.inProgress = true;
      this.addressService
        .save(this.selectedAddress)
        .pipe(untilDestroyed(this))
        .subscribe((result: AddressResult) => {
          this.inProgress = false;
          if (!result) {
            // error of validation or saving
            return;
          }

          if (result.savedAddress) {
            this.close(result.savedAddress);
            return;
          }

          this.isExistedAddress = result.exists;
          this.suggestedAddresses = result.addresses || [];

          if (
            this.isDeliveryAddress &&
            this.isExistedAddress &&
            !isEmptyArray(this.suggestedAddresses)
          ) {
            // on product page - close dialog and return the address
            this.close(this.suggestedAddresses[0]);
          }

          if (this.suggestedAddresses.length === 1) {
            // autoselect suggested address
            this.selectedSuggestionUUID = this.suggestedAddresses[0].uuid;
          }
        });
    } else {
      Object.entries(this.addressForm.controls).forEach(
        (control: [string, any]) => {
          if (control[1] instanceof FormControl) {
            control[1].markAsTouched();
          }
        }
      );
    }
  }

  private getStatesByCountryCode(isoCountry: string): CountryState[] {
    const foundCountry = this.getCountryByCode(isoCountry);
    return foundCountry ? foundCountry.states : [];
  }

  getCountryByCode(countryCode: string): Country {
    return this.countries.find((c) => c.isoCode === countryCode);
  }

  get hasSuggestions(): boolean {
    return (
      Array.isArray(this.suggestedAddresses) &&
      this.suggestedAddresses.length > 0
    );
  }

  get title(): string {
    if (this.hasSuggestions) {
      return 'Verify Address';
    }

    return this.isNewAddress ? 'Add New Address' : 'Edit Address';
  }

  selectedSuggestion(address: Address) {
    this.selectedSuggestionUUID = address ? address.uuid : null;
  }

  backToEdit() {
    this.isExistedAddress = false;
    this.selectedSuggestionUUID = null;
    this.suggestedAddresses = [];
  }

  savedValidated() {
    const address = this.suggestedAddresses.find(
      (a) => a.uuid === this.selectedSuggestionUUID
    );
    if (!address) {
      return;
    }

    address.is_default = this.selectedAddress.is_default;
    if (this.selectedAddress.id) {
      address.id = this.selectedAddress.id;
    }
    if (this.selectedAddress.type) {
      address.type = this.selectedAddress.type;
    }

    this.inProgress = true;
    this.addressService
      .save(address)
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.inProgress = false;
        if (result && result.savedAddress) {
          this.close(result.savedAddress);
        }
      });
  }

  get isEmployee(): boolean {
    return this.authService.isEmployee;
  }

  get existedAddressWarning(): string {
    if (this.isEmployee) {
      if (isEmptyArray(this.suggestedAddresses)) {
        return 'This address already exists and cannot be added again.';
      }

      const address = this.suggestedAddresses[0];
      if (address.iShippingLocation) {
        return 'This address already exists. Please search for this Wholesaler in the Ship-To address dropdown with the Wholesaler name or number. This address can not be added to your address book.';
      }
      return 'This address already exists and can not be added to your address book.';
    }

    return 'This address already exists and cannot be added again.';
  }
}
